import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { useAutoSave } from "@/hooks/useAutoSave";
import { Zap } from "lucide-react";
import SectionHeader from "../shared/SectionHeader";

interface AgentPersonalityTabProps {
  agent: any;
  updateAgent: (data: Partial<any>) => Promise<boolean>;
}

// Create schemas
const creativitySchema = z.object({
  ai_temperature: z.number().min(0).max(2).default(0.15),
});

type CreativityFormValues = z.infer<typeof creativitySchema>;

const AgentPersonalityTab = ({
  agent,
  updateAgent,
}: AgentPersonalityTabProps) => {
  const [initialFormValues, setInitialFormValues] = useState<any | null>({
    ai_temperature: agent.ai_temperature ?? 0.15,
  });

  const creativityForm = useForm<CreativityFormValues>({
    resolver: zodResolver(creativitySchema),
    defaultValues: {
      ai_temperature: agent.ai_temperature ?? 0.15,
    },
  });

  const { isSaving, setDirty } = useAutoSave({
    onSave: async () => {
      if (!initialFormValues) return;
      const creativityData = creativityForm.getValues();

      return await updateAgent(creativityData);
    },
    successMessage: "Personality settings saved automatically",
    errorMessage: "Failed to auto-save personality settings",
  });

  // Set up form change subscriptions for auto-save
  useEffect(() => {
    if (!initialFormValues) return;

    const handleChange = () => {
      const currentValues = creativityForm.getValues();
      const hasChanges = Object.keys(currentValues).some(
        (key) => currentValues[key] !== initialFormValues[key]
      );
      if (hasChanges) {
        setInitialFormValues(currentValues);
        setDirty();
      }
    };
    const creativitySubscription = creativityForm.watch(handleChange);
    return () => {
      creativitySubscription.unsubscribe();
    };
  }, [creativityForm, setDirty, initialFormValues]);

  return (
    <div className="w-full space-y-6">
      {/* Creativity Level Section */}
      <Card className="border rounded-2xl">
        <CardContent className="p-5">
          <SectionHeader
            icon={<Zap className="h-5 w-5" />}
            title="Creativity Level"
          />

          <Form {...creativityForm}>
            <form className="space-y-4">
              <FormField
                control={creativityForm.control}
                name="ai_temperature"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-gray-700">
                      Response Creativity
                    </FormLabel>
                    <FormControl>
                      <div className="space-y-4">
                        <Slider
                          min={0}
                          max={2}
                          step={0.05}
                          value={[field.value]}
                          onValueChange={(value) => field.onChange(value[0])}
                          className="w-full"
                        />
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>Precise & Consistent</span>
                          <span className="font-medium">
                            {field.value.toFixed(2)}
                          </span>
                          <span>Creative & Spontaneous</span>
                        </div>
                      </div>
                    </FormControl>
                    <p className="text-xs text-gray-500 mt-2">
                      Lower values make responses more predictable and focused.
                      Higher values encourage more creative and varied
                      responses.
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </CardContent>
      </Card>

      {isSaving && (
        <div className="flex items-center justify-end">
          <span className="text-sm text-[#666] flex items-center">
            <div className="h-3 w-3 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
            Auto-saving...
          </span>
        </div>
      )}
    </div>
  );
};

export default AgentPersonalityTab;
